/**
 * Precisão da fila, medida com as fotos guardadas: cada foto disse "a fila
 * de Q ambientes escoa em D dias úteis"; os términos que vieram depois dizem
 * em quantos dias úteis ela escoou de fato. A faixa de erro que a tela
 * escreve ao lado de cada número sai daqui.
 */
import { duEntre, ehDiaUtil, somarDu } from "./calendario";

/**
 * Faixa usada enquanto não houver 3 medições maduras. Veio da análise de
 * 07/10/2026 (plano, achado 4), que mediu "abertos ÷ ritmo das últimas 26
 * semanas", e não da projeção desta página (janela −28..−3 com trava de
 * cobertura e fila sem obra futura): com ela, as mesmas cópias dão só uma
 * medição madura. O rótulo diz a régua (ADR-0059) até a medida própria
 * substituir esta.
 */
export const PRECISAO_REFERENCIA = Object.freeze({
  erroMin: -0.08,
  erroMax: 0.19,
  n: 3,
  fonte: "3 testes com cópias antigas da planilha (out/25 a fev/26), análise de 07/10/2026",
});

export const FONTE_MEDIDA = "medido";
export const MEDICOES_MINIMAS = 3;
// O término é lançado com até ~2 semanas de atraso: um escoamento só é
// "real" quando já passou dessa janela, senão ainda pode ganhar términos.
export const MATURACAO_DU = 10;
const AMOSTRA_PARA_PERCENTIS = 10;

/** Percentil com interpolação linear (p entre 0 e 1). */
export function percentil(valores, p) {
  const ordenados = [...valores].sort((a, b) => a - b);
  if (ordenados.length === 0) return null;
  const posicao = (ordenados.length - 1) * p;
  const abaixo = Math.floor(posicao);
  const acima = Math.ceil(posicao);
  return ordenados[abaixo] + (ordenados[acima] - ordenados[abaixo]) * (posicao - abaixo);
}

/**
 * Uma foto por dia: com várias no mesmo `data_referencia`, vale a primeira
 * tirada (menor `criado_em`; sem ele, a primeira da lista).
 */
function umaFotoPorDia(fotos) {
  const porDia = new Map();
  (fotos || []).forEach((foto) => {
    const atual = porDia.get(foto.data_referencia);
    if (!atual) {
      porDia.set(foto.data_referencia, foto);
      return;
    }
    if (foto.criado_em && atual.criado_em && foto.criado_em < atual.criado_em) {
      porDia.set(foto.data_referencia, foto);
    }
  });
  return [...porDia.values()].sort((a, b) => (a.data_referencia < b.data_referencia ? -1 : 1));
}

/**
 * Em quantos dias úteis depois de `t` a soma dos términos atinge `Q`.
 * Término em fim de semana/feriado conta no dia útil seguinte (é ali que a
 * capacidade "fecha" o intervalo). `null` se ainda não atingiu.
 */
function escoamentoReal({ Q, t, datasTermino, naoUteis }) {
  const ordens = datasTermino
    .filter((data) => data > t)
    .map((data) => duEntre(t, data, naoUteis) + (ehDiaUtil(data, naoUteis) ? 0 : 1))
    .sort((a, b) => a - b);
  if (ordens.length < Q) return null;
  const Dreal = ordens[Q - 1];
  return { Dreal, dia: somarDu(t, Dreal, naoUteis) };
}

function faixaMedida(medicoes) {
  const erros = medicoes.map((m) => m.erro);
  const usarPercentis = erros.length >= AMOSTRA_PARA_PERCENTIS;
  return {
    erroMin: usarPercentis ? percentil(erros, 0.1) : Math.min(...erros),
    erroMax: usarPercentis ? percentil(erros, 0.9) : Math.max(...erros),
    n: erros.length,
    fonte: FONTE_MEDIDA,
  };
}

/**
 * `medirPrecisao({fotos, terminos, naoUteis, hoje})` →
 * `{erroMin, erroMax, n, fonte, medicoes, pendentes}`.
 * - `fotos`: FOTO_META do backend (só as com `projecao` entram).
 * - `terminos`: a união de `unirTerminos` (já sem serviço/cancelado).
 * Com 3+ medições maduras a faixa é a medida (mín/máx; p10/p90 a partir de
 * 10); senão `PRECISAO_REFERENCIA`.
 */
export function medirPrecisao({ fotos, terminos, naoUteis, hoje } = {}) {
  const limiteMaturacao = somarDu(hoje, -MATURACAO_DU, naoUteis);
  const datasTermino = (terminos || []).map((t) => t?.termino).filter(Boolean);
  const medicoes = [];
  const pendentes = [];
  umaFotoPorDia(fotos).forEach((foto) => {
    const projecao = foto.projecao;
    if (!projecao || !(projecao.fila > 0) || !(projecao.du_previstos > 0)) return;
    const base = {
      data_referencia: foto.data_referencia,
      fila: projecao.fila,
      ritmo: projecao.ritmo_por_du,
      previsto: projecao.du_previstos,
    };
    const real = escoamentoReal({ Q: projecao.fila, t: projecao.hoje, datasTermino, naoUteis });
    if (!real || real.dia > limiteMaturacao) {
      pendentes.push(base);
      return;
    }
    medicoes.push({
      ...base,
      real: real.Dreal,
      dataReal: real.dia,
      erro: real.Dreal / projecao.du_previstos - 1,
    });
  });
  const faixa = medicoes.length >= MEDICOES_MINIMAS ? faixaMedida(medicoes) : PRECISAO_REFERENCIA;
  return { ...faixa, medicoes, pendentes };
}
